import axios, { type AxiosInstance, type AxiosError } from 'axios';
import { useAuthStore } from '@/store/authStore';
import { redirectToCorpLogout } from '@/lib/corpAuth';
import type { ApiError } from '@/types/api.types';

// ─── Axios Instance ────────────────────────────────────────────────────────────

// Axios timeout must outlast API Gateway httpApi's 30s integration cap so
// the browser receives the clean 504 response from API Gateway (and treats
// it as a normal HTTP failure) rather than aborting the connection
// prematurely while the Lambda might still complete and write DB rows.
// Important for terminal endpoints where the orphan-charge scenario depends
// on knowing whether the server-side actually committed.
export const apiClient: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? '/api/v5',
  timeout: 35_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (!value || Object.prototype.toString.call(value) !== '[object Object]') {
    return false;
  }

  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

// ─── Request Interceptor: Inject JWT ─────────────────────────────────────────

apiClient.interceptors.request.use(
  (config) => {
    const { token, accountId } = useAuthStore.getState();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (accountId) {
      config.headers['X-Target-Company'] = String(accountId);
    }
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error: unknown) => Promise.reject(error),
);

// ─── Response Interceptor: Normalize errors, handle 401 ──────────────────────

apiClient.interceptors.response.use(
  (response) => {
    // Don't touch binary / streaming responses (responseType blob/arraybuffer/stream).
    // The interceptor below sets `response.data.data = null` on Blobs which
    // was harmless but confusing; on arraybuffers it actually breaks downstream
    // consumers that expect raw bytes.
    const responseType = response.config?.responseType;
    if (responseType === 'blob' || responseType === 'arraybuffer' || responseType === 'stream') {
      return response;
    }

    // Node v1 API returns raw JSON; PHP v5 wraps in { data: ... }.
    // Normalize so services always see { data: { data: ... } } shape.
    // React Query requires query functions to never return undefined.
    if (isPlainObject(response.data) && !('data' in response.data)) {
      response.data = { data: response.data };
    } else if (!isPlainObject(response.data)) {
      // DELETE/empty responses return "" or null — wrap so downstream
      // code can safely access response.data.data without throwing.
      response.data = { data: response.data || null };
    }
    // Guarantee .data.data is never undefined (React Query requirement)
    if (response.data && response.data.data === undefined) {
      response.data.data = null;
    }
    return response;
  },
  (error: AxiosError<ApiError>) => {
    // Access Restrictions: a 403 with this code means the account can't be used
    // from the current network/location. Treat it exactly like a logout — clear
    // auth and bounce to the corporate login (login.prospera.ai) — except on
    // login requests, which fall through so the login/corp-callback screen shows
    // the message inline (via error.userMessage below) instead of looping.
    if (
      error.response?.status === 403 &&
      error.response.data?.error?.code === 'ACCESS_RESTRICTED'
    ) {
      const url = error.config?.url ?? '';
      const isLoginRequest =
        url.includes('/auth/staff-login') ||
        url.includes('/auth/staff/login') ||
        url.includes('/auth/corp/exchange') ||
        url.includes('/auth/login');
      if (!isLoginRequest) {
        useAuthStore.getState().logout();
        redirectToCorpLogout();
        return Promise.reject(error);
      }
    }

    if (error.response?.status === 401) {
      const url = error.config?.url ?? '';
      const isLoginRequest =
        url.includes('/auth/staff-login') ||
        url.includes('/auth/staff/login') ||
        url.includes('/auth/corp/exchange') ||
        url.includes('/auth/login');
      if (!isLoginRequest) {
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    const apiErr = error.response?.data;
    const message =
      apiErr?.error?.message ??
      apiErr?.message ??
      (error.code === 'ECONNABORTED'
        ? 'Request timed out. Please try again.'
        : !error.response
          ? 'Network error. Please check your connection.'
          : undefined);

    if (message) {
      (error as AxiosError & { userMessage: string }).userMessage = message;
    }

    return Promise.reject(error);
  },
);

// ─── Utility: extract field errors from API response ─────────────────────────

export function extractFieldErrors(error: unknown): Record<string, string> {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiError | undefined;
    const fieldErrors = data?.errors ?? data?.error?.details;
    if (fieldErrors && typeof fieldErrors === 'object') {
      return Object.fromEntries(
        Object.entries(fieldErrors as Record<string, string[]>).map(([field, msgs]) => [
          field,
          Array.isArray(msgs) ? msgs[0] : String(msgs),
        ]),
      );
    }
  }
  return {};
}

/**
 * The machine-readable code the API pairs with every error message
 * (`{ error: { code, message } }`), for the callers that branch on a specific
 * failure instead of just displaying it.
 */
export function getErrorCode(error: unknown): string | null {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiError | undefined;
    return data?.error?.code ?? null;
  }
  return null;
}

/**
 * Strip developer-oriented junk from a server-supplied error message so it's
 * presentable to clinic staff. Targets the most common leak shapes:
 *   - Embedded JSON dumps: e.g. `{"detail":"Internal Server Error",...}`
 *   - Trailing `[HTTP NNN]` status suffix
 *   - Trailing `| error: ...` curl/proxy detail tail
 *   - Doubled prefix like `Failed to X: Failed to X: foo` (a known
 *     re-throw pattern in TerminalForwardService where a catch handler
 *     re-prefixes an already-prefixed inner exception message)
 *
 * Does NOT replace legitimate human-readable messages like
 * `Card declined: insufficient funds` — those pass through unchanged.
 *
 * Exported so the TerminalCheckoutPanel and any other custom error
 * surfaces can apply the same normalization before display.
 */
export function humanizeServerMessage(raw: string): string {
  let m = (raw ?? '').trim();
  if (m === '') return '';
  // Strip embedded single-level JSON dumps. Use a non-greedy match that
  // does not allow nested braces (safe because v5 error bodies don't
  // nest objects in their message strings).
  m = m.replace(/\{[^{}]*\}/g, '').trim();
  // Strip trailing `[HTTP NNN]`
  m = m.replace(/\s*\[HTTP\s*\d+\s*\]\s*$/i, '').trim();
  // Strip trailing `| error: <curl/proxy detail>`
  m = m.replace(/\s*\|\s*error:.*$/i, '').trim();
  // Collapse "X: X: rest" -> "X: rest" (TerminalForwardService:764
  // pattern). Match the prefix non-greedily up to first colon-space.
  const doubled = m.match(/^(.+?:\s*)(\1)(.*)$/);
  if (doubled) {
    m = doubled[1] + doubled[3];
  }
  // Trim trailing colons / commas / dashes left behind by the strips.
  m = m.replace(/[\s:,\-]+$/, '').trim();
  return m;
}

/**
 * True when a (preferably already-humanized) server message looks like a
 * raw internal-exception leak rather than a deliberate user-facing
 * message. These typically come from `catch (\Throwable $e) {
 *   return errorResponse('<context> failed: ' . $e->getMessage(), ...);
 * }` in v5 controllers and shouldn't be shown to clinic staff.
 *
 * Callers should substitute their domain-specific fallback when this
 * returns true.
 */
export function looksLikeLeakyInternalError(msg: string): boolean {
  if (!msg) return true;
  const leakyPatterns = [
    /^Failed to authorize card/i,
    /^Terminal checkout failed/i,
    /^Terminal tokenize failed/i,
    /^Terminal test failed/i,
    /^Clear session failed/i,
    /^Get terminal details failed/i,
    /^Forward .* failed/i,
    /^Gateway error/i,
    /^Server error/i,
    /^Internal Server Error/i,
    /^SQLSTATE/i,
    /^cURL error/i,
  ];
  return leakyPatterns.some((re) => re.test(msg));
}

export function getErrorMessage(error: unknown, fallback = 'An unexpected error occurred'): string {
  if (axios.isAxiosError(error)) {
    const raw =
      (error as AxiosError & { userMessage?: string }).userMessage ??
      (error.response?.data as ApiError)?.error?.message ??
      (error.response?.data as ApiError)?.message;
    if (raw) {
      const cleaned = humanizeServerMessage(raw);
      // Drop to fallback when the cleaned message is empty OR looks
      // like an internal-exception leak. Keeps legitimate user-facing
      // messages (e.g. "Card declined") flowing through.
      if (cleaned && !looksLikeLeakyInternalError(cleaned)) {
        return cleaned;
      }
    }
    return fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}
